import React, { createContext, useContext, useState, useEffect } from "react";
import * as CampaignService from "services/campaign-service";

export const CampaignsContext = createContext();

export const CampaignsProvider = ({ children }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignStats, setCampaignStats] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch all campaigns
  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await CampaignService.getCampaigns();
      setCampaigns(data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching campaigns:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch single campaign
  const fetchCampaign = async (campaignId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await CampaignService.getCampaignById(campaignId);
      setSelectedCampaign(data);
      return data;
    } catch (err) {
      setError(err.message);
      console.error("Error fetching campaign:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create campaign
  const createCampaign = async (campaignData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await CampaignService.createCampaign(campaignData);
      setRefreshTrigger((prev) => prev + 1);
      return result;
    } catch (err) {
      setError(err.message);
      console.error("Error creating campaign:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update campaign
  const updateCampaign = async (campaignId, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await CampaignService.updateCampaign(campaignId, data);
      setRefreshTrigger((prev) => prev + 1);
      return result;
    } catch (err) {
      setError(err.message);
      console.error("Error updating campaign:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete campaign
  const deleteCampaign = async (campaignId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await CampaignService.deleteCampaign(campaignId);
      setRefreshTrigger((prev) => prev + 1);
      return result;
    } catch (err) {
      setError(err.message);
      console.error("Error deleting campaign:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Toggle campaign status
  const toggleStatus = async (campaignId, isActive) => {
    setLoading(true);
    setError(null);
    try {
      const result = await CampaignService.toggleCampaignStatus(campaignId, isActive);
      setRefreshTrigger((prev) => prev + 1);
      return result;
    } catch (err) {
      setError(err.message);
      console.error("Error toggling campaign status:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch campaign stats
  const fetchStats = async (campaignId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await CampaignService.getCampaignStats(campaignId);
      setCampaignStats(data);
      return data;
    } catch (err) {
      setError(err.message);
      console.error("Error fetching campaign stats:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Generate promo codes
  const generateCodes = async (campaignId, quantity) => {
    setLoading(true);
    setError(null);
    try {
      const data = await CampaignService.generatePromoCodes(campaignId, quantity);
      setRefreshTrigger((prev) => prev + 1);
      return data;
    } catch (err) {
      setError(err.message);
      console.error("Error generating promo codes:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch promo codes
  const fetchPromoCodes = async (campaignId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await CampaignService.getPromoCodes(campaignId);
      setPromoCodes(data);
      return data;
    } catch (err) {
      setError(err.message);
      console.error("Error fetching promo codes:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch claims
  const fetchClaims = async (campaignId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await CampaignService.getCampaignClaims(campaignId);
      setClaims(data);
      return data;
    } catch (err) {
      setError(err.message);
      console.error("Error fetching claims:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch campaigns when refreshTrigger changes
  useEffect(() => {
    fetchCampaigns();
  }, [refreshTrigger]);

  // Clear selected campaign when unmounting
  useEffect(() => {
    return () => {
      setSelectedCampaign(null);
      setCampaignStats(null);
      setPromoCodes([]);
      setClaims([]);
    };
  }, []);

  return (
    <CampaignsContext.Provider
      value={{
        campaigns,
        selectedCampaign,
        campaignStats,
        promoCodes,
        claims,
        loading,
        error,
        fetchCampaigns,
        fetchCampaign,
        createCampaign,
        updateCampaign,
        deleteCampaign,
        toggleStatus,
        fetchStats,
        generateCodes,
        fetchPromoCodes,
        fetchClaims,
        setSelectedCampaign,
        setError,
      }}
    >
      {children}
    </CampaignsContext.Provider>
  );
};

export const useCampaigns = () => useContext(CampaignsContext);
